import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/feature/Navbar';
import { Footer } from '@/components/feature/Footer';
import { SEOHead } from '@/components/feature/SEOHead';
import { SchemaMarkup, organizationSchema, webSiteSchema } from '@/components/feature/SchemaMarkup';
import { categoryLinks, articleCards } from '@/mocks/categories';
import { featuredProducts } from '@/mocks/products';
import i18n from '@/i18n';

const heroCopy: Record<string, { headline: string; subheadline: string; description: string; cta1: string; cta2: string; searchPlaceholder: string; badge: string; stats: string[] }> = {
  en: {
    headline: 'Stop Searching. Start Buying.',
    subheadline: '1,000+ AllChinaBuy Products with Verified Links & Real Prices',
    description: 'Skip the hours of forum scrolling. We curate the best products from AllChinaBuy with direct buy links, verified sellers, and updated prices. Click. Buy. Done.',
    cta1: 'Shop All Products',
    cta2: 'Buy on Main Site',
    searchPlaceholder: 'Search products, categories, or brands...',
    badge: 'Updated Weekly — 14 Categories — 112 Products',
    stats: ['1,000+ Products', '14 Categories', 'Verified Links', 'Weekly Updates'],
  },
  fr: {
    headline: 'Arrêtez de Chercher. Commencez à Acheter.',
    subheadline: '1,000+ Produits AllChinaBuy avec Liens Vérifiés & Vrais Prix',
    description: 'Plus besoin de défiler des forums pendant des heures. Nous sélectionnons les meilleurs produits AllChinaBuy avec liens d\'achat directs, vendeurs vérifiés et prix à jour. Cliquez. Achetez. C\'est fait.',
    cta1: 'Tous les Produits',
    cta2: 'Acheter sur le Site',
    searchPlaceholder: 'Rechercher des produits, catégories ou marques...',
    badge: 'Mis à Jour Hebdomadairement — 14 Catégories — 112 Produits',
    stats: ['1,000+ Produits', '14 Catégories', 'Liens Vérifiés', 'Mises à Jour Hebdo'],
  },
  de: {
    headline: 'Hören Sie auf zu Suchen. Fangen Sie an zu Kaufen.',
    subheadline: '1,000+ AllChinaBuy-Produkte mit Verifizierten Links & Echten Preisen',
    description: 'Kein stundenlanges Scrollen durch Foren mehr. Wir kuratieren die besten AllChinaBuy-Produkte mit direkten Kauflinks, verifizierten Verkäufern und aktualisierten Preisen. Klicken. Kaufen. Fertig.',
    cta1: 'Alle Produkte Shoppen',
    cta2: 'Auf Hauptseite Kaufen',
    searchPlaceholder: 'Produkte, Kategorien oder Marken suchen...',
    badge: 'Wöchentlich Aktualisiert — 14 Kategorien — 112 Produkte',
    stats: ['1,000+ Produkte', '14 Kategorien', 'Verifizierte Links', 'Wöchentliche Updates'],
  },
  da: {
    headline: 'Stop med at Søge. Start med at Købe.',
    subheadline: '1,000+ AllChinaBuy Produkter med Verificerede Links & Rigtige Priser',
    description: 'Slip for at rulle gennem fora i timevis. Vi kuraterer de bedste AllChinaBuy-produkter med direkte købslinks, verificerede sælgere og opdaterede priser. Klik. Køb. Færdig.',
    cta1: 'Shop Alle Produkter',
    cta2: 'Køb på Hovedsiden',
    searchPlaceholder: 'Søg efter produkter, kategorier eller mærker...',
    badge: 'Opdateret Ugentligt — 14 Kategorier — 112 Produkter',
    stats: ['1,000+ Produkter', '14 Kategorier', 'Verificerede Links', 'Ugentlige Opdateringer'],
  },
};

export default function Home() {
  const { t } = useTranslation();
  const [lang, setLang] = useState(i18n.language || 'en');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const handleLangChange = () => setLang(i18n.language || 'en');
    i18n.on('languageChanged', handleLangChange);
    return () => {
      i18n.off('languageChanged', handleLangChange);
    };
  }, []);

  const copy = heroCopy[lang] || heroCopy.en;
  const articleSectionCards = articleCards.slice(0, 4);

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title="AllChinaBuy Spreadsheet — 1,000+ Products with Verified Buy Links & Real Prices"
        description="Stop searching. Start buying. 1,000+ curated AllChinaBuy products with direct links, verified sellers, real prices, and weekly updates. 14 categories."
      />
      <SchemaMarkup schema={[organizationSchema(), webSiteSchema()]} />
      <Navbar />

      {/* Hero Section — High Conversion */}
      <section className="relative min-h-[520px] md:min-h-[580px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Premium%20fashion%20products%20arranged%20on%20clean%20white%20marble%20surface%2C%20luxury%20sneakers%2C%20designer%20bags%2C%20streetwear%20accessories%2C%20editorial%20product%20photography%2C%20warm%20natural%20lighting%2C%20minimal%20aesthetic%2C%20high-end%20ecommerce%20showcase%2C%20soft%20shadows&width=1920&height=1080&seq=hero-bg-1&orientation=landscape"
            alt="AllChinaBuy products showcase"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        </div>

        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center pt-24 pb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs md:text-sm font-medium mb-6">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            {copy.badge}
          </div>

          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white max-w-5xl mx-auto leading-[1.1] tracking-tight"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {copy.headline}
          </h1>
          <p
            className="mt-4 text-lg md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed font-medium"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            {copy.subheadline}
          </p>
          <p className="mt-4 text-sm md:text-base text-white/60 max-w-xl mx-auto leading-relaxed">
            {copy.description}
          </p>

          {/* Stats Row */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3 md:gap-5">
            {copy.stats.map((stat, i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/15 text-white/80 text-xs md:text-sm font-medium">
                <i className="ri-check-line text-emerald-400" />
                {stat}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/products"
              className="px-8 py-4 rounded-full bg-white text-foreground-950 font-bold text-sm hover:bg-white/90 transition-colors whitespace-nowrap shadow-lg shadow-black/20"
            >
              <span className="flex items-center gap-2">
                <i className="ri-shopping-bag-3-line" />
                {copy.cta1}
              </span>
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-8 py-4 rounded-full border-2 border-white/40 text-white font-bold text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              <span className="flex items-center gap-2">
                <i className="ri-external-link-line" />
                {copy.cta2}
              </span>
            </a>
          </div>

          {/* Search Box */}
          <div className="mt-6 max-w-lg mx-auto">
            <div className="relative flex items-center">
              <i className="ri-search-line absolute left-4 text-white/50 text-lg" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={copy.searchPlaceholder}
                className="w-full pl-11 pr-4 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>
          </div>

          {/* Category Dropdown */}
          <div className="mt-4 relative max-w-xs mx-auto">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between px-5 py-2.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white text-sm hover:bg-white/15 transition-colors"
            >
              <span className="flex items-center gap-2">
                <i className="ri-list-check text-white/70" />
                {t('categories.title')}
              </span>
              {dropdownOpen ? (
                <i className="ri-arrow-up-s-line text-white/70" />
              ) : (
                <i className="ri-arrow-down-s-line text-white/70" />
              )}
            </button>
            {dropdownOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-background-200/70 overflow-hidden z-50 max-h-80 overflow-y-auto">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                  >
                    <i className={`${cat.icon} text-primary-500`} />
                    {t(`categories.${cat.nameKey}`)}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Category Buttons Grid */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('homeSections.spreadsheetHubTitle')}
          </h2>
          <p className="mt-2 text-foreground-600 max-w-xl mx-auto">
            {t('homeSections.spreadsheetHubDesc')}
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4 max-w-6xl mx-auto">
          {categoryLinks.map((cat) => (
            <a
              key={cat.nameKey}
              href={cat.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative flex flex-col items-center gap-2 p-3 md:p-4 rounded-xl bg-white border border-background-200/70 hover:border-primary-300 hover:shadow-md transition-all"
            >
              <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-primary-50 flex items-center justify-center group-hover:bg-primary-100 transition-colors">
                <i className={`${cat.icon} text-lg md:text-xl text-primary-600`} />
              </div>
              <span className="text-xs md:text-sm font-medium text-foreground-700 text-center group-hover:text-primary-700 transition-colors leading-tight">
                {t(`categories.${cat.nameKey}`)}
              </span>
            </a>
          ))}
        </div>
      </section>

      {/* Featured Products */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                {t('featuredProducts')}
              </h2>
              <p className="mt-1 text-sm text-foreground-600">
                {t('featuredProductsDesc')}
              </p>
            </div>
            <Link
              to="/products"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-background-200/70 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
            >
              {t('viewAll')} <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
            {featuredProducts.map((product) => (
              <a
                key={product.id}
                href={product.productUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col bg-white rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className="relative aspect-square overflow-hidden bg-background-100">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-2.5 md:p-3 flex flex-col flex-1">
                  <h3 className="text-xs font-medium text-foreground-900 line-clamp-2 leading-snug flex-1 group-hover:text-primary-700 transition-colors">
                    {product.name}
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="text-xs font-bold text-primary-700">
                      {product.priceUsd}
                    </span>
                    <span className="text-[10px] text-foreground-400">
                      {product.priceCny}
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          <div className="text-center mt-6 sm:hidden">
            <Link
              to="/products"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-background-200/70 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
            >
              {t('viewAllProducts')} <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('homeSections.articlesTitle')}
          </h2>
          <p className="mt-2 text-foreground-600 max-w-xl mx-auto">
            {t('homeSections.articlesDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 max-w-7xl mx-auto">
          {articleSectionCards.map((article) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="group flex flex-col bg-white rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-300 hover:shadow-md transition-all"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={t(article.titleKey)}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-white/90 text-xs font-medium text-foreground-700 capitalize">
                  {article.category}
                </span>
              </div>
              <div className="p-4 flex flex-col flex-1">
                <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-700 transition-colors">
                  {t(article.titleKey)}
                </h3>
                <p className="mt-2 text-xs text-foreground-500 line-clamp-2 flex-1">
                  {t(article.descKey)}
                </p>
                <div className="mt-3 flex items-center justify-between text-xs text-foreground-400">
                  <span>{article.date}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    {article.readTime} {t('minutesRead')}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/article/allchinabuy-spreadsheet-guide"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-background-200/70 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
          >
            {t('viewAll')} <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      {/* Features Grid */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {[
              { titleKey: 'homeSections.reviewTitle', descKey: 'homeSections.reviewDesc', icon: 'ri-star-line', color: 'bg-amber-50 text-amber-600' },
              { titleKey: 'homeSections.shippingTitle', descKey: 'homeSections.shippingDesc', icon: 'ri-truck-line', color: 'bg-emerald-50 text-emerald-600' },
              { titleKey: 'homeSections.couponTitle', descKey: 'homeSections.couponDesc', icon: 'ri-coupon-line', color: 'bg-rose-50 text-rose-600' },
              { titleKey: 'homeSections.warehouseTitle', descKey: 'homeSections.warehouseDesc', icon: 'ri-building-2-line', color: 'bg-sky-50 text-sky-600' },
              { titleKey: 'homeSections.legitTitle', descKey: 'homeSections.legitDesc', icon: 'ri-shield-check-line', color: 'bg-violet-50 text-violet-600' },
              { titleKey: 'homeSections.comparisonTitle', descKey: 'homeSections.comparisonDesc', icon: 'ri-scales-line', color: 'bg-orange-50 text-orange-600' },
            ].map((item) => (
              <Link
                key={item.titleKey}
                to="/article/allchinabuy-spreadsheet-guide"
                className="group flex items-start gap-4 p-5 md:p-6 rounded-xl bg-white border border-background-200/70 hover:border-primary-300 hover:shadow-md transition-all"
              >
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${item.color}`}>
                  <i className={`${item.icon} text-lg md:text-xl`} />
                </div>
                <div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-700 transition-colors">
                    {t(item.titleKey)}
                  </h3>
                  <p className="mt-1 text-xs md:text-sm text-foreground-500">
                    {t(item.descKey)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Flat%20lay%20fashion%20ecommerce%20products%20on%20warm%20neutral%20background%2C%20sneakers%2C%20bags%2C%20accessories%2C%20streetwear%2C%20editorial%20photography%2C%20soft%20warm%20tones%2C%20clean%20minimal%20aesthetic%2C%20premium%20shopping%20experience&width=1920&height=600&seq=cta-bg-1&orientation=landscape"
            alt="AllChinaBuy products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/55" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>
            {t('homeSections.finalCtaTitle')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-white/80 max-w-lg mx-auto">
            {t('homeSections.finalCtaDesc')}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 rounded-full bg-white text-foreground-950 font-semibold text-sm hover:bg-white/90 transition-colors whitespace-nowrap"
            >
              {t('homeSections.finalCtaButton')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener noreferrer"
              className="px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold text-sm hover:bg-white/10 transition-colors whitespace-nowrap"
            >
              {t('buyItNow')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}